import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dog,
  Camera,
  Trash2,
  MapPin,
  Printer
} from "lucide-react";
import { printElementById } from "@/utils/printUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DogsTab = ({
  puppies,
  clients,
  intakes,
  enrollments,
  getAuthHeaders,
  fetchAllData
}) => {
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [selectedPuppy, setSelectedPuppy] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [puppyDetailOpen, setPuppyDetailOpen] = useState(false);
  const [selectedPuppyDetail, setSelectedPuppyDetail] = useState(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedPuppy) return;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/puppies/${selectedPuppy.id}/photo`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Photo uploaded successfully!");
      setPhotoUploadOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to upload photo");
    }
    setUploadingPhoto(false);
  };

  const handleDeletePuppy = async (puppyId, puppyName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/trainer/puppies/${puppyId}`, { headers });
      toast.success(`${puppyName} has been deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete dog");
    }
  };

  const getPuppyEnrollments = (puppyId) => {
    return enrollments.filter(e => e.puppy_id === puppyId);
  };

  const handlePrint = (elementId, title) => {
    printElementById(elementId, title);
  };

  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {puppies.length === 0 ? (
          <div className="col-span-full card-premium text-center py-12">
            <Dog className="w-16 h-16 text-olive/50 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-stone">No dogs registered</p>
          </div>
        ) : (
          puppies.map((puppy) => (
            <div key={puppy.id} className="card-premium">
              {/* Photo Section */}
              <div className="relative mb-4">
                {puppy.photos && puppy.photos.length > 0 ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${puppy.photos[0]}`}
                    alt={puppy.name}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-full h-48 bg-sand/50 rounded-lg flex items-center justify-center">
                    <Dog className="w-16 h-16 text-olive/30" strokeWidth={1.5} />
                  </div>
                )}
                <Dialog open={photoUploadOpen && selectedPuppy?.id === puppy.id} onOpenChange={(open) => {
                  setPhotoUploadOpen(open);
                  if (open) setSelectedPuppy(puppy);
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-charcoal"
                    >
                      <Camera className="w-4 h-4 mr-1" />
                      {puppy.photos && puppy.photos.length > 0 ? "Change" : "Add Photo"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Photo for {puppy.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="border-2 border-dashed border-olive/30 rounded-lg p-8 text-center">
                        <Camera className="w-12 h-12 text-olive/50 mx-auto mb-4" />
                        <p className="text-stone mb-4">Upload a photo of {puppy.name}</p>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          disabled={uploadingPhoto}
                          className="max-w-xs mx-auto"
                        />
                        {uploadingPhoto && (
                          <p className="text-olive mt-2">Uploading...</p>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Info Section */}
              <h3 
                className="font-heading text-xl text-charcoal mb-1 cursor-pointer hover:text-olive transition-colors"
                onClick={() => {
                  setSelectedPuppyDetail(puppy);
                  setPuppyDetailOpen(true);
                }}
              >
                {puppy.name}
              </h3>
              <p className="text-stone text-sm mb-2">{puppy.breed}</p>
              <div className="flex items-center gap-2 mb-3">
                <Badge className={
                  puppy.vaccination_status === "up_to_date" ? "bg-olive" :
                  puppy.vaccination_status === "partial" ? "bg-terracotta" : "bg-sand text-charcoal"
                }>
                  {puppy.vaccination_status?.replace(/_/g, " ")}
                </Badge>
                <span className="text-stone text-sm">{puppy.age_weeks} weeks</span>
              </div>
              <p className="text-stone text-xs truncate">{puppy.pickup_address}</p>
              <div className="mt-3 pt-3 border-t border-olive/10">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="w-full text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete Dog
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Dog</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete <strong>{puppy.name}</strong>? 
                        This will also delete all enrollments, report cards, homework, and graduation records for this dog.
                        This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => handleDeletePuppy(puppy.id, puppy.name)}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Puppy Detail Dialog */}
      <Dialog open={puppyDetailOpen} onOpenChange={setPuppyDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="font-heading text-2xl">{selectedPuppyDetail?.name}</DialogTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handlePrint('puppy-print-content', `${selectedPuppyDetail?.name} - Dog Profile`)}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </DialogHeader>
          {selectedPuppyDetail && (
            <div id="puppy-print-content" className="space-y-6 mt-4">
              {/* Photo and Basic Info */}
              <div className="flex gap-6">
                <div className="w-32 h-32 rounded-xl overflow-hidden bg-sand/50 flex-shrink-0">
                  {selectedPuppyDetail.photos?.length > 0 ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${selectedPuppyDetail.photos[0]}`}
                      alt={selectedPuppyDetail.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Dog className="w-12 h-12 text-olive/30" />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <p className="text-lg text-charcoal"><strong>Breed:</strong> {selectedPuppyDetail.breed}</p>
                  <p className="text-charcoal"><strong>Age:</strong> {selectedPuppyDetail.age_weeks} weeks</p>
                  <p className="text-charcoal"><strong>DOB:</strong> {selectedPuppyDetail.date_of_birth}</p>
                  <div className="flex items-center gap-2">
                    <strong>Vaccinations:</strong>
                    <Badge className={
                      selectedPuppyDetail.vaccination_status === "up_to_date" ? "bg-olive" :
                      selectedPuppyDetail.vaccination_status === "partial" ? "bg-terracotta" : "bg-sand text-charcoal"
                    }>
                      {selectedPuppyDetail.vaccination_status?.replace(/_/g, " ")}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Owner Info */}
              <div className="bg-sand/50 rounded-lg p-4">
                <h4 className="font-medium text-charcoal mb-2">Owner Information</h4>
                {(() => {
                  const owner = clients.find(c => c.id === selectedPuppyDetail.user_id);
                  return owner ? (
                    <div className="space-y-1 text-sm">
                      <p><strong>Name:</strong> {owner.name}</p>
                      <p><strong>Email:</strong> {owner.email}</p>
                      <p><strong>Phone:</strong> {owner.phone}</p>
                    </div>
                  ) : <p className="text-stone text-sm">Owner info not available</p>;
                })()}
              </div>

              {/* Pickup Address */}
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-olive mt-0.5" />
                <div>
                  <p className="font-medium text-charcoal">Pickup Address</p>
                  <p className="text-stone text-sm">{selectedPuppyDetail.pickup_address || "Not specified"}</p>
                </div>
              </div>

              {/* Temperament Notes */}
              {selectedPuppyDetail.temperament_notes && (
                <div>
                  <h4 className="font-medium text-charcoal mb-2">Temperament Notes</h4>
                  <p className="text-stone text-sm bg-white p-3 rounded-lg border border-olive/10">
                    {selectedPuppyDetail.temperament_notes}
                  </p>
                </div>
              )}

              {/* Enrollments */}
              <div>
                <h4 className="font-medium text-charcoal mb-3">Enrollments & Intakes</h4>
                {getPuppyEnrollments(selectedPuppyDetail.id).length === 0 ? (
                  <p className="text-stone text-sm">No enrollments yet</p>
                ) : (
                  <div className="space-y-3">
                    {getPuppyEnrollments(selectedPuppyDetail.id).map(enrollment => {
                      const intake = intakes.find(i => i.id === enrollment.intake_id);
                      return (
                        <div key={enrollment.id} className="bg-white p-4 rounded-lg border border-olive/10">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-charcoal">{enrollment.program_name}</h5>
                            <Badge className={
                              enrollment.status === "confirmed" || enrollment.status === "in_progress" ? "bg-olive" :
                              enrollment.status === "completed" ? "bg-charcoal" :
                              enrollment.status === "pending" ? "bg-terracotta" : "bg-sand text-charcoal"
                            }>
                              {enrollment.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-stone space-y-1">
                            <p><strong>Start Date:</strong> {enrollment.start_date}</p>
                            <p><strong>Day:</strong> {enrollment.day_of_week}</p>
                            <p><strong>Sessions:</strong> {enrollment.sessions_completed || 0} / 4 completed</p>
                            {intake && (
                              <p><strong>Intake Status:</strong> {intake.status}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
